import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    RadioGroup, FormControlLabel, Radio, FormLabel, FormControl, Box,
    Select, MenuItem, Checkbox, ListItemText, OutlinedInput, InputLabel, Typography,
} from '@mui/material';
import { INotice, INoticeRequest, NoticeAudienceType } from '../../interfaces/notice.interface';
import { Department } from '../../interfaces/department.interface';
import { DepartmentService } from '../../services/department.service';
import { NoticeService } from '../../services/notice.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

interface Props {
    open: boolean;
    notice?: INotice | null;
    onClose: () => void;
    onSaved: () => void;
}

const toLocalInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NoticeForm: React.FC<Props> = ({ open, notice, onClose, onSaved }) => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [publishAt, setPublishAt] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [audienceType, setAudienceType] = useState<NoticeAudienceType>('ALL');
    const [departmentIds, setDepartmentIds] = useState<string[]>([]);
    const [attachment, setAttachment] = useState<File | null>(null);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        DepartmentService.getDepartmentsByInstitution()
            .then(r => setDepartments(r.content))
            .catch(err => toast.showApiError(err, 'Failed to load departments'));
    }, [open]);

    useEffect(() => {
        if (notice) {
            setTitle(notice.title);
            setBody(notice.body);
            setPublishAt(toLocalInput(notice.publishAt));
            setExpiresAt(toLocalInput(notice.expiresAt));
            setAudienceType(notice.audienceType);
            setDepartmentIds(notice.departmentIds || []);
        } else {
            setTitle('');
            setBody('');
            setPublishAt('');
            setExpiresAt('');
            setAudienceType('ALL');
            setDepartmentIds([]);
        }
        setAttachment(null);
    }, [notice, open]);

    const handleSubmit = async () => {
        if (!title.trim() || !body.trim() || !expiresAt) {
            toast.showError('Title, body, and expiry are required');
            return;
        }
        if (audienceType === 'DEPARTMENTS' && departmentIds.length === 0) {
            toast.showError('Select at least one department');
            return;
        }
        const request: INoticeRequest = {
            title: title.trim(),
            body,
            publishAt: publishAt ? new Date(publishAt).toISOString() : null,
            expiresAt: new Date(expiresAt).toISOString(),
            audienceType,
            departmentIds: audienceType === 'DEPARTMENTS' ? departmentIds : [],
        };
        setSaving(true);
        try {
            if (notice) {
                await NoticeService.update(notice.id, institutionId, request);
            } else {
                await NoticeService.create(institutionId, request, attachment);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to save notice');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ 'data-help-page': 'notices.form.page' } as any}>
            <DialogTitle>{notice ? 'Edit Notice' : 'New Notice'}</DialogTitle>
            <DialogContent>
                <Box data-help-section="notices.form.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField data-help="notices.form.title" label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth required inputProps={{ maxLength: 200 }} />
                    <TextField data-help="notices.form.body" label="Body" value={body} onChange={e => setBody(e.target.value)} fullWidth required multiline minRows={4} inputProps={{ maxLength: 10000 }} />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField data-help="notices.form.publishAt" label="Publish At (optional)" type="datetime-local" value={publishAt} onChange={e => setPublishAt(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                        <TextField data-help="notices.form.expiresAt" label="Expires At" type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} InputLabelProps={{ shrink: true }} required fullWidth />
                    </Box>
                    <FormControl data-help="notices.form.audience">
                        <FormLabel>Audience</FormLabel>
                        <RadioGroup row value={audienceType} onChange={e => setAudienceType(e.target.value as NoticeAudienceType)}>
                            <FormControlLabel value="ALL" data-help="notices.form.audienceAll" control={<Radio />} label="All employees" />
                            <FormControlLabel value="DEPARTMENTS" data-help="notices.form.audienceDepts" control={<Radio />} label="Selected departments" />
                        </RadioGroup>
                    </FormControl>
                    {audienceType === 'DEPARTMENTS' && (
                        <FormControl fullWidth data-help="notices.form.departments">
                            <InputLabel>Departments</InputLabel>
                            <Select
                                multiple
                                value={departmentIds}
                                onChange={e => setDepartmentIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                input={<OutlinedInput label="Departments" />}
                                renderValue={(selected) => departments.filter(d => selected.includes(d.id)).map(d => d.name).join(', ')}
                            >
                                {departments.map(d => (
                                    <MenuItem key={d.id} value={d.id}>
                                        <Checkbox checked={departmentIds.includes(d.id)} />
                                        <ListItemText primary={d.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {!notice && (
                        <Box>
                            <Button variant="outlined" component="label" data-help="notices.form.attachment">
                                {attachment ? `Attachment: ${attachment.name}` : 'Add attachment (optional)'}
                                <input hidden type="file" onChange={e => setAttachment(e.target.files?.[0] || null)} />
                            </Button>
                            {attachment && (
                                <Button size="small" onClick={() => setAttachment(null)} data-help="notices.form.removeAttachment" sx={{ ml: 1 }}>Remove</Button>
                            )}
                        </Box>
                    )}
                    {notice && notice.hasAttachment && (
                        <Typography variant="caption" color="text.secondary">
                            Existing attachment: {notice.attachmentOriginalName} (attachment changes are not supported in v1)
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} data-help="notices.form.cancel">Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving} data-help="notices.form.submit">
                    {notice ? 'Save' : 'Publish'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NoticeForm;
